import React, { useState, useEffect } from 'react'
import { Route } from 'react-router-dom'
import { isEqual, reduce, values } from 'lodash'
import { useHistory } from 'react-router-dom'
import useDeepCompareEffect from 'use-deep-compare-effect'
import cn from 'classnames'

import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import OrderInformation from './OrderInformation'
import SidebarModal from '../../SidebarModal'
import Button from '../../Button'
import List from '../../List'
import BackChevron from '../../BackChevron'
import TabOrderTotals from '../TabOrderTotals'
import { ReactComponent as Checkmark } from '../../../assets/icons/checkmark-icon.svg'

import { makeDiscounts, makeSelectedExemptions, calculateTaxAndTotal } from '../../../utils/exemptionUtils'
import useManagerApproval from '../../../hooks/useManagerApproval'

import styles from './Close.module.scss'
import { centsToDollars } from '../../../utils/formatters'

const requiresApproval = (item, index) => {
  const isApplyDiscounts = index === 0 && item.selected === false
  const isSalesTaxOrServiceFee = index > 0 && item.selected === true

  if (isApplyDiscounts || isSalesTaxOrServiceFee) {
    return true
  } else {
    return false
  }
}

const getSelectedDiscountIndexes = (discounts) => {
  return reduce(discounts, (acc, discount, index) => {
    if (discount.selected) acc.push(index)
    return acc
  }, [])
}

const ExemptableItem = ({ loading, label, selected, onChange, disabled }) => {
  let checkmarkElement = null

  if (selected) {
    checkmarkElement = <Checkmark className={styles.discountCheckmarkIcon} />
  }

  const _onClick = () => {
    if (!disabled) {
      onChange()
    }
  }

  const setStyles = () => {
    if (disabled) {
      return cn(styles.labelContainer, styles.labelContainerDisabled)
    }
    return styles.labelContainer
  }

  let content = (
    <div className={setStyles()} htmlFor={label} onClick={_onClick}>
      <input disabled={disabled} id={label} type="checkbox" checked={selected} onChange={onChange} />
      <span className={styles.discountCheckmark}>{checkmarkElement}</span>
      {label}
    </div>
  )

  if (loading) {
    content = (
      <SkeletonTheme color="#3c3e52" highlightColor="#52556f">
        <Skeleton height={22} width="75%"  />
      </SkeletonTheme>
    )
  }

  return (
    <li className={styles.discount}>
      {content}
    </li>
  )
}

const AdjustExemptions = ({
  amount,
  amountDisplay,
  fractionalQuantity,
  className,
  exemptionOverrides,
  getRemoteOrderTotal,
  groupedItems,
  hideModifiedAt,
  hidePayment,
  isOrderLatestFromServer,
  itemCount,
  items,
  modifiedAt,
  onClose,
  onUpdateExemptions,
  onViewCheck,
  orderId,
  orderingPreference,
  orderNumber,
  remoteOrderTotals,
  payment,
  paymentOnOrder,
  paymentOptions,
  paymentType,
  subtotal,
  suiteInformation,
  pendingMutation,
  userNotes,
}) => {
  const classNames = cn(styles.close, className)
  const history = useHistory()
  const { loading, failed } = remoteOrderTotals

  const orderLoading = loading || !(pendingMutation === undefined)

  const [approvalModal, requestApproval] = useManagerApproval()
  const [exemptions, setExemptions] = useState(exemptionOverrides)
  const [updateDisabled, setUpdatedDisabled] = useState(true)
  const [discounts, setDiscounts] = useState(makeDiscounts(remoteOrderTotals, orderingPreference, exemptions))
  const [initialSelectedDiscountIndices, setInitialSelectedDiscountIndices] = useState(getSelectedDiscountIndexes(discounts))

  const taxAndTotal = calculateTaxAndTotal(remoteOrderTotals, exemptions)
  const [grandTotal, setGrandTotal] = useState(taxAndTotal.total)
  const [tax, setTax] = useState(taxAndTotal.tax)

  const remoteOrderTotal = exemptions.applyDiscount ? remoteOrderTotals?.totalsWithDiscount ?? {} : remoteOrderTotals?.totalsWithDiscountNotApplied ?? {}
  let serviceChargeDisplay = remoteOrderTotal?.serviceChargeDisplay
  serviceChargeDisplay = remoteOrderTotal?.serviceChargeInCents + Math.abs(remoteOrderTotal?.serviceChargeDiscountInCents)
  serviceChargeDisplay = centsToDollars(serviceChargeDisplay)
  let discountDisplay = remoteOrderTotal?.discountDisplay ?? '-'


  if (!exemptions.applyServiceFee) {
    serviceChargeDisplay = undefined

    if (remoteOrderTotal?.serviceChargeDiscountInCents) {
      const calculatedDiscount = remoteOrderTotal?.discountAmountInCents - remoteOrderTotal?.serviceChargeDiscountInCents
      discountDisplay = centsToDollars(calculatedDiscount)
    }
  }

  let grandTotalElement = (
    <TabOrderTotals
      className={styles.tabOrderTotals}
      discount={discountDisplay}
      exemptions={exemptions}
      itemCount={itemCount}
      nonExemptableTaxRules={values(remoteOrderTotal?.nonExemptableTaxRules ?? [])}
      serviceCharge={serviceChargeDisplay}
      serviceChargeName={remoteOrderTotal?.serviceChargeName || 'Service Charge'}
      subtotal={subtotal}
      tax={tax}
      total={grandTotal}
    />
  )

  if (!orderLoading && failed) {
    grandTotalElement = <p>There was an issue updating totals for this order.</p>
  }

  const updateExemptionsAndTotals = (newExemptions) => {
    setExemptions(newExemptions)

    const newDiscounts = makeDiscounts(remoteOrderTotals, orderingPreference, newExemptions)
    setDiscounts(newDiscounts)

    const selectedIndices = getSelectedDiscountIndexes(newDiscounts)
    setUpdatedDisabled(isEqual(selectedIndices, initialSelectedDiscountIndices))

    const taxAndTotal = calculateTaxAndTotal(remoteOrderTotals, newExemptions)
    setTax(taxAndTotal.tax)
    setGrandTotal(taxAndTotal.total)
  }

  const handleDiscountsDidChange = (selectedIndices) => {
    const newExemptions = makeSelectedExemptions(selectedIndices)

    setUpdatedDisabled(isEqual(selectedIndices, initialSelectedDiscountIndices))

    updateExemptionsAndTotals(newExemptions)
  }

  useEffect(() => {
    if (items.length) {
      getRemoteOrderTotal({ orderUuid: orderId, items, splitOrder: false, fractionalQuantity })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length])

  // If ordering preferences or totals are changing, then the system hasn't reached a default
  // exemption consensus, so the exemptions should be overwritten with what is dictated by the
  // totals and preferences.
  useDeepCompareEffect(() => {
    updateExemptionsAndTotals(exemptionOverrides)
  }, [remoteOrderTotals, orderingPreference])

  let buttons = [
    <Button key='update-exemptions' onClick={() => onUpdateExemptions(exemptions)} className={[styles.actionButton, styles.mintGreenHighlight]} disabled={updateDisabled || orderLoading || orderingPreference.pendingMutation !== undefined || !isOrderLatestFromServer}>Update</Button>
  ]

  let content = (
    <>
      <OrderInformation account={suiteInformation?.displayName} hidePayment={hidePayment} hideModifiedAt={hideModifiedAt} payment={payment} amount={amountDisplay} modifiedAt={modifiedAt} orderNumber={orderNumber} note={userNotes} />
      <div>
        <List
          items={discounts}
          component={ExemptableItem}
          selectionType="multiple"
          defaultSelectedIndexes={initialSelectedDiscountIndices}
          selectionDidChange={handleDiscountsDidChange}
          className={styles.discounts}
          beforeChange={(item, index, proceed) => {
            if (requiresApproval(item, index)) {
              requestApproval(() => {
                proceed()
              })
            } else {
              proceed()
            }
          }}
        />
        {grandTotalElement}
      </div>
    </>
  )

  const leftAccessoryElement = (
    <>
      <Route exact path="/tabs/:orderId/adjust-exemptions/actions">
        <BackChevron onClick={() => {
          history.replace(`/tabs/${orderId}/adjust-exemptions`)
        }} />
      </Route>
      <Route exact path="/tabs/:orderId/adjust-exemptions">
        <BackChevron onClick={() => {
          history.replace(`/tabs/${orderId}`)
        }} />
      </Route>
    </>
  )

  return (
    <SidebarModal
      show
      title="Modify Order Settings"
      onClose={onClose}
      leftAccessory={leftAccessoryElement}
      className={classNames}
    >
      <div className={styles.bodyContainer}>
        <Route exact path="/tabs/:orderId/adjust-exemptions/actions">
          <OrderInformation account={suiteInformation?.displayName} hidePayment={hidePayment} hideModifiedAt={hideModifiedAt} payment={payment} amount={amountDisplay} modifiedAt={modifiedAt} orderNumber={orderNumber} note={userNotes} />
        </Route>
        <Route exact path="/tabs/:orderId/adjust-exemptions">
          {content}
          <div className={styles.buttonContainer}>
            {buttons}
          </div>
          {approvalModal}
        </Route>
      </div>
    </SidebarModal>
  )
}

export default AdjustExemptions
